// pfs-checkbox-field.ts — self-contained Checkbox-Control (PFS-619).
//
// Checkbox + Inline-Label in der EIGENEN Shadow. Anders als die Text-/Select-
// Controls liegt das Label NEBEN der Box (Zeile), und der Zustand ist boolesch.
//
// API: data-label (Inline-Text) · data-name · data-checked (INITIAL, Praesenz =
//   angehakt) · data-error · data-hint · data-readonly.
//   Property el.checked (get/set boolean). Re-emittiert change composed.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { installFieldChecked, bindFieldControl } from "../../composition/field-value.js";

const STYLES = `
:host { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.row { display: flex; align-items: center; gap: 0.55rem; }
.row input { width: auto; margin: 0; flex: none; accent-color: var(--pfs-color-primary); }
.row .lbl { font-size: 0.9rem; font-weight: 500; color: var(--pfs-color-on-surface); cursor: pointer; }
/* Textlose Box (Modus 2: Feld-Label kommt vom <pfs-field>-Wrapper) → kein
   Phantom-Gap. Konsistent mit pfs-text-field/-radio-group. */
.row .lbl:empty { display: none; }
.row input:disabled + .lbl { opacity: 0.75; cursor: default; }
.msg { font-size: 0.8rem; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }
`;

defineComponent({
  tagName: "pfs-checkbox-field",
  mixins: [withShadow()],
  // .checked ab Konstruktion pre-connect-sicher (PFS-650, uniform mit den Wert-Controls).
  created: installFieldChecked,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const label = document.createElement("label");
    label.className = "row";
    const box = document.createElement("input");
    box.type = "checkbox";
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    label.append(box, lblEl);
    root.appendChild(label);
    const msg = document.createElement("p");
    msg.className = "msg";
    root.appendChild(msg);

    // .checked an die Box binden (pre-connect-sicher, uniform mit den Wert-Controls):
    // wendet den gewünschten Zustand an (Puffer / data-checked) + hält ihn synchron.
    bindFieldControl(host, box);

    // natives change re-emittieren (composed überquert die Shadow-Grenze).
    box.addEventListener("change", () => {
      host.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
    });

    const paint = (): void => {
      lblEl.textContent = host.dataset.label ?? "";
      if (host.dataset.name != null) box.name = host.dataset.name;
      box.disabled = host.dataset.readonly != null;

      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
      } else if (hint) {
        msg.textContent = hint;
        msg.className = "msg hint";
      } else {
        msg.textContent = "";
        msg.className = "msg";
      }
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-label", "data-error", "data-hint", "data-readonly", "data-name"],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
